// (C) 2022 GoodData Corporation
import { ArithmeticFormat } from "./fmt_arithmetic.js";
import { FormatterUsingIcu, StringCharacterIterator, isInvalidArgumentError, } from "./fmt_base.js";
import { ConditionalFormat } from "./fmt_conditional.js";
import { NumberFormat } from "./fmt_number.js";
import { DefaultFormat } from "./gooddata_format.js";
export class FormatterFactory {
    constructor(matchers) {
        this.matchers = matchers;
    }
    splitAlternatives(format) {
        let current = "";
        let escape = false;
        const result = [];
        const iterator = new StringCharacterIterator(format, 0, format.length);
        while (iterator.hasNext()) {
            const c = iterator.next();
            if (c === "\\") {
                escape = true;
                // append escaping backslash as-is; concrete formatter compilers expect to find these and
                // will act accordingly
                current += c;
                continue;
            }
            if (c === ";" && !escape) {
                // unescaped semicolon delimits a conditional format alternative
                // push currently accumulated format string to result (this creates a copy) and then
                // prepare accumulator for next alternative
                result.push(current);
                current = "";
                continue;
            }
            current += c;
            escape = false;
        }
        result.push(current);
        return result;
    }
    /**
     * C++ strings are terminated by the first embedded null, which is not true for JS strings, so we do it explicitly to keep consistent.
     * Truncated string literal with embedded NUL character example: `".win.ini\\u0000#,#.00"`
     *
     * @param format - format to truncate
     * @returns Description for the return value
     */
    truncatedStringLiteral(format) {
        const hasEmbeddedNull = format.search("\u0000");
        if (hasEmbeddedNull >= 0) {
            return format.substring(0, hasEmbeddedNull);
        }
        return format;
    }
    /**
     * Compiles a formatter instance for the provided format string.
     *
     * @param format - format to compile
     * @returns an instance of formatter ready to use for formatting numbers
     */
    compileFormatter(format) {
        //Truncated string literal with embedded NUL character
        let effectiveFormat = this.truncatedStringLiteral(format);
        // if format string contains [excel] tag, then remove the tag and everything that follows it
        //
        // see https://github.com/gooddata/gdc-numberjs/blob/0ab90ee596a854b8ad38fa7956d7148b8c1291df/lib/number.js#L585
        effectiveFormat = effectiveFormat.replace(new RegExp(this.matchers.getExcelFormatSuffixMatcher(), "gi"), "");
        let formatter;
        try {
            if (this.matchers.getArithmeticMatcher().test(effectiveFormat)) {
                formatter = ArithmeticFormat.compile(this.matchers, this, effectiveFormat);
            }
            else {
                const alternatives = this.splitAlternatives(effectiveFormat);
                if (alternatives.length === 1 && !this.matchers.getConditionMatcher().test(effectiveFormat)) {
                    formatter = NumberFormat.compile(this.matchers, effectiveFormat);
                }
                else {
                    formatter = ConditionalFormat.compile(this.matchers, this, alternatives);
                }
            }
        }
        catch (error) {
            if (isInvalidArgumentError(error)) {
                // follows reference implementation; any problem while compiling the original pattern results in fallback
                // to the default formatter
                formatter = NumberFormat.compile(this.matchers, DefaultFormat);
            }
            else {
                console.error(error);
                throw error;
            }
        }
        return new SpecialValueGuard(formatter);
    }
}
/**
 * Formatter decorator which first tests the number to format for some special cases such as NaN or Infinity
 * and returns special string representation immediately.
 *
 * Otherwise the decorated formatter is called and its value returned as-is.
 */
export class SpecialValueGuard extends FormatterUsingIcu {
    constructor(decorated) {
        super();
        this.decorated = decorated;
    }
    doFormat(ctx, number, metadata) {
        if (number === null) {
            // NULLs may be subject to specific conditional formats
            return this.decorated.doFormat(ctx, number, metadata);
        }
        else if (Number.isNaN(number)) {
            return ctx.constants.nanString;
        }
        else if (!Number.isFinite(number)) {
            return number < 0 ? ctx.constants.negativeInfinity : ctx.constants.positiveInfinity;
        }
        return this.decorated.doFormat(ctx, number, metadata);
    }
    doResolve(ctx, number) {
        if (Number.isNaN(number)) {
            // this follows reference implementation; for NaN, the resulting format string should be empty string
            return "";
        }
        return this.decorated.doResolve(ctx, number);
    }
}
//# sourceMappingURL=factory.js.map